import React, { useEffect, useState, useContext } from 'react';
import { Grid, Tabs, TabsProps } from '@amzn/awsui-components-core';
import constants from '../../common/constants';
import { isUUID } from 'class-validator';
import '../../resources/styles/scss/components/collection/collection.scss';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import EditCollectionRightColumn from './EditCollectionRightColumn';
import EditCollectionsEpicsTab from './EditCollectionsEpicsTab';
import { withRouter } from 'react-router';
import EditCollectionPermissionsTab from './EditCollectionPermissionsTab';
import EditCollectionHeader from './EditCollectionHeader';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { AppContext } from '../../contexts/AppContext';
import NotFound from '../common/NotFound';

import {
  Collection,
  CollectionAudience,
  CollectionEpic,
  CollectionState,
  CollectionStory,
  ContentType,
  Owner,
  CollectionEventType,
} from '@amzn/awscat-pattern-library-service-model';
import CollectionEpicWrapper from './CollectionEpicWrapper';
import CollectionStoryWithIndex from './CollectionStoryWithIndex';
import { DateTime } from 'luxon';
import BrowserHistory from '../utils/BrowserHistory';
import { COLLECTION_VIEWER_PREFIX } from '../../common/RouterPaths';
import { formatEpics, validateCollection } from './utils';

enum CollectionActiveTabId {
  EPICS = 'epics',
  PERMISSONS = 'permissions',
}

export interface CollectionDataProps {
  location: any;
  match: any;
}

export interface EditCollectionProps {
  collection?: Collection;
}

export interface EditCollectionHeaderProps {
  collection?: Collection;
  setCollection: (collection: Collection | undefined) => void;
}

export interface EditCollectionsEpicsTabProps {
  epics: CollectionEpicWrapper[];
  setEpics: (epics: CollectionEpicWrapper[]) => void;
  saveCollection?: () => Promise<void>;
  epicIdx?: number;
  disableAction?: boolean;
  collectionId?: string;
}

export interface EditCollectionRightColProps {
  collectionId: string;
  state: CollectionState;
  publishCollection: () => Promise<void>;
  saveCollection: () => Promise<void>;
  disableAction: boolean;
}

function EditCollection(props: CollectionDataProps) {
  const mplService: IMplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker = FlashbarMessageBroker.create();
  const appContext = useContext(AppContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [activeTabId, setActiveTabId] = useState<CollectionActiveTabId>(
    CollectionActiveTabId.EPICS,
  );
  const [collection, setCollection] = useState<Collection>();
  const [epics, setEpics] = useState<CollectionEpicWrapper[]>([]);
  const [lastModified, setLastModified] = useState<DateTime>();
  const [owners, setOwners] = useState<Owner[]>([]);
  const [audience, setAudience] = useState<CollectionAudience>(CollectionAudience.PRIVATE);
  const [disableAction, setDisableAction] = useState<boolean>(false);

  useEffect(() => {
    const { collectionId } = props.match.params;
    if (!isUUID(collectionId)) {
      return;
    }
    const getCollection = async () => {
      const response = await mplService.getCollection(collectionId);
      const collectionEpics = response.collection.epics;
      const { locked, lockedBy } = response.collection;
      // @ts-expect-error TS2339 - strict migration
      const { userId } = appContext?.authnInfo?.userProfile;
      if (!locked) {
        await mplService.createCollectionEvent(CollectionEventType.LOCK, collectionId);
      } else {
        if (userId !== lockedBy) {
          flashbarMessageBroker.pushErrorMessage(`Collection is currently edited by ${lockedBy}`);
          setDisableAction(true);
        }
      }
      if (collectionEpics && collectionEpics.length > 0) {
        const epicsWrapped = collectionEpics.map((epic) => {
          const stories: CollectionStoryWithIndex[] = epic.stories.map(
            (story, idx) => new CollectionStoryWithIndex(story, idx),
          );
          // @ts-expect-error TS2345 - strict migration
          return new CollectionEpicWrapper(epic.title, epic.description, stories);
        });
        setEpics(epicsWrapped);
      } else {
        const emptyStory = [CollectionStory.create('', '', '', '', ContentType.Pattern)];
        response.collection.epics = [CollectionEpic.create('', '', emptyStory)];
      }
      setCollection(response.collection);
      // @ts-expect-error TS2345 - strict migration
      setOwners(response.collection.owners);
      setAudience(response.collection.audience);
      setLastModified(response.collection.lastModified);
    };

    getCollection();
    setIsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (collection === undefined) {
      return;
    }
  }, [collection]);

  const canPublishCollection = () => {
    if (audience !== CollectionAudience.INTERNAL) {
      flashbarMessageBroker.pushErrorMessage('Cannot publish private collection.');
      return false;
    }
    return true;
  };

  const hasInputError = () => {
    // @ts-expect-error TS2345 - strict migration
    const { errors, hasError } = validateCollection({
      ...collection,
      epics: formatEpics(epics),
      owners,
      audience,
    });
    if (hasError) {
      flashbarMessageBroker.pushErrorMessage(errors.join(' '));
      return true;
    }
    return false;
  };

  const saveCollection = async () => {
    if (!hasInputError()) {
      // @ts-expect-error TS2345 - strict migration
      await mplService.editCollection({
        ...collection,
        epics: formatEpics(epics),
        owners,
        audience,
      });
      // @ts-expect-error TS2339 - strict migration
      const { collectionId } = collection;
      BrowserHistory.push(`${COLLECTION_VIEWER_PREFIX}/${collectionId}`);
    }
  };

  const publishCollection = async () => {
    if (!hasInputError() && canPublishCollection()) {
      // @ts-expect-error TS2345 - strict migration
      await mplService.publishCollection({
        ...collection,
        epics: formatEpics(epics),
        owners,
        audience,
      });
      // @ts-expect-error TS2339 - strict migration
      const { collectionId } = collection;
      BrowserHistory.push(`${COLLECTION_VIEWER_PREFIX}/${collectionId}`);
    }
  };

  const renderEditView = () => {
    return (
      <div>
        <EditCollectionHeader collection={collection} setCollection={setCollection} />
        <Grid
          gridDefinition={[
            { colspan: { default: 3, xxs: 9 } },
            { colspan: { default: 9, xxs: 3 } },
          ]}
        >
          {renderTabs()}
          <EditCollectionRightColumn
            collectionId={collection?.collectionId ?? ''}
            state={collection?.isPublished ? CollectionState.PUBLISHED : CollectionState.DRAFT}
            publishCollection={publishCollection}
            saveCollection={saveCollection}
            disableAction={disableAction}
          />
        </Grid>
      </div>
    );
  };

  const handleTabChange = (event: CustomEvent<TabsProps.ChangeDetail>) => {
    changeTabView(event.detail);
  };

  // @ts-expect-error TS7006 - strict migration
  const changeTabView = (tabId) => {
    setActiveTabId(tabId);
  };

  const renderTabs = () => {
    const allTabs = [
      {
        content: (
          <EditCollectionsEpicsTab
            epics={epics}
            setEpics={setEpics}
            saveCollection={saveCollection}
            disableAction={disableAction}
            collectionId={collection?.collectionId ?? ''}
          />
        ),
        id: 'epics',
        label: constants.EPICS_TAB_LABEL,
      },
      {
        content: (
          <EditCollectionPermissionsTab
            owners={owners}
            setOwners={setOwners}
            audience={audience}
            setAudience={setAudience}
            // @ts-expect-error TS2322 - strict migration
            collectionId={collection?.collectionId}
          />
        ),
        id: 'permissions',
        label: constants.PERMISSIONS_TAB_LABEL,
      },
    ];

    return (
      // @ts-expect-error TS2322 - strict migration
      <Tabs tabs={allTabs} ariaLabel="Collection editing navigation" onChange={handleTabChange} />
    );
  };

  if (!isUUID(props.match.params.collectionId)) {
    return <NotFound />;
  }

  return isLoading ? null : renderEditView();
}

export default withRouter(EditCollection);
